// =============================
// File: src/components/user/ReferralDashboard.jsx
// =============================
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, TrendingUp, Calendar } from 'lucide-react';
import axios from 'axios';
import ReferralStats from './referral/ReferralStats';
import ActiveReferralsList from './referral/ActiveReferralsList';
import DailyEarningsView from './referral/DailyEarningsView';
import ReferralCodeShare from './referral/ReferralCodeShare';

const API_URL = process.env.REACT_APP_API_URL || '';

function ReferralDashboard({ userId, setActiveComponent }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'daily'

  useEffect(() => {
    fetchReferralSummary();
  }, [userId]);

  async function fetchReferralSummary() {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/referral-summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching referral summary:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-montserrat">Cargando información de referidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-[#8B2659] text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setActiveComponent('Options')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-montserrat font-semibold">Volver al Menú</span>
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold font-montserrat">Programa de Referidos</h1>
              <p className="text-white/80 text-sm font-montserrat">Gana dinero compartiendo Fast Max</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Referral Code Share */}
        <ReferralCodeShare referralCode={summary?.referral_code} />

        {/* Stats Overview */}
        <ReferralStats summary={summary} />

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1 flex gap-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-2.5 px-4 rounded-lg font-montserrat font-semibold text-sm transition-all ${
              activeTab === 'overview'
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Users className="w-4 h-4" />
              Mis Referidos
            </div>
          </button>
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex-1 py-2.5 px-4 rounded-lg font-montserrat font-semibold text-sm transition-all ${
              activeTab === 'daily'
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              Ganancias Diarias
            </div>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' ? (
          <ActiveReferralsList summary={summary} />
        ) : (
          <DailyEarningsView userId={userId} />
        )}
      </div>
    </div>
  );
}

export default ReferralDashboard;